import React, { useState, useMemo } from 'react'

import { LinkOutlined } from '@ant-design/icons'
import { MultipleValue } from '@shared/table/Rules/component'
import FullDayRangePicker from '@components/shared/FullDayRangePicker'

import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule } from '@shared/table/TableIndex'
import { Tag } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

interface MerchantOption {
  id: string;
  name: string;
}

interface ImportsListProps {
  role?: string;
  service: TableService; // New
  merchants?: MerchantOption[];
  hideMerchantUI?: boolean; // Operator single-merchant 模式使用
}

interface ImportRow {
  id: string;
  name?: string | null;
  import_type?: string;
  status?: string;
  inserted_rows?: number | null;
  updated_rows?: number | null;
  merchant?: string | null;
  operator?: string | null;
  user?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  [key: string]: unknown;
}

const ImportsList: React.FC<ImportsListProps> = ({
  role = 'admin',
  service, // New
  merchants = [],
  hideMerchantUI = false // Operator single-merchant 模式使用
}) => {
  const { t } = useTranslation()
  const [lastModified, setLastModified] = useState(0)

  const columns = useMemo<unknown[]>(() => {
    const cols: unknown[] = [
      {
        nameKey: `shared.imports.list.columns.id`,
        persist: true,
        sortable: true,
        sortFieldName: 'imports.id',
        width: '200px',
        cell: ({ id, name }: ImportRow) => (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%'
          }}>
            <Link to={`/imports/${id}`}>
              {name || id}
            </Link>
            <a href={`/${role}/imports/${id}`} target="_blank" rel="noopener noreferrer">
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        ),
      },
      {
        nameKey: `shared.imports.list.columns.import_type`,
        sortable: false,
        wrap: true,
        cell: ({ import_type }: ImportRow) => (
          <Tag color="blue">
            {t(`common.import_type.${import_type}`)}
          </Tag>
        ),
      },
      {
        nameKey: `shared.imports.list.columns.status`,
        wrap: true,
        cell: ({ status }: ImportRow) => {
          const statusConfig: Record<string, { color: string; label: string }> = {
            pending: { color: 'default', label: t('common.import_status.pending') },
            parsing: { color: 'processing', label: t('common.import_status.parsing') },
            importing: { color: 'processing', label: t('common.import_status.importing') },
            partially_completed: { color: 'warning', label: t('common.import_status.partially_completed') },
            completed: { color: 'success', label: t('common.import_status.completed') },
            failed: { color: 'error', label: t('common.import_status.failed') }
          }
          const config = (status && statusConfig[status]) || { color: 'default', label: status as string }
          return <Tag color={config.color}>{config.label}</Tag>
        },
      },
      {
        nameKey: `shared.imports.list.columns.inserted_rows`,
        sortable: false,
        wrap: true,
        width: '120px',
        cell: ({ inserted_rows }: ImportRow) => (
          <span>{inserted_rows != null ? inserted_rows : '-'}</span>
        ),
      },
      {
        nameKey: `shared.imports.list.columns.updated_rows`,
        sortable: false,
        wrap: true,
        width: '120px',
        cell: ({ updated_rows }: ImportRow) => (
          <span>{updated_rows != null ? updated_rows : '-'}</span>
        ),
      }
    ];

    // Add merchant column for operator role (single-merchant 模式隱藏)
    if (role === 'operator' && !hideMerchantUI) {
      cols.push({
        nameKey: `shared.imports.list.columns.merchant`,
        sortable: false,
        wrap: true,
        selector: ({ merchant }: ImportRow) => merchant,
      });
    }

    cols.push(
      {
        nameKey: `shared.imports.list.columns.operator`,
        sortable: false,
        wrap: true,
        cell: ({ operator, user }: ImportRow) => operator || user || '-',
      },
      {
        nameKey: `common.created_at`,
        sortable: true,
        sortFieldName: 'imports.created_at',
        width: '180px',
        wrap: true,
        selector: ({ created_at }: ImportRow) => created_at,
      },
      {
        nameKey: `common.updated_at`,
        sortable: true,
        sortFieldName: 'imports.updated_at',
        width: '180px',
        wrap: true,
        selector: ({ updated_at }: ImportRow) => updated_at,
      }
    );

    return cols;
  }, [role, t, hideMerchantUI])

  const supportRules = useMemo<SupportRule[]>(() => {
    const rules: SupportRule[] = [
      {
        label: t(`shared.imports.list.filters.import_type`),
        component: MultipleValue,
        inputName: 'imports.import_type',
        defaultPinned: true,
        createable: false,
        defaultOptions: [
          { label: t('common.import_type.order'), value: 'order' },
          { label: t('common.import_type.product'), value: 'product' },
          { label: t('common.import_type.inbound'), value: 'inbound' },
          { label: t('common.import_type.processing_order'), value: 'processing_order' },
          { label: t('common.import_type.return_order'), value: 'return_order' },
          { label: t('common.import_type.sales_channel'), value: 'sales_channel' },
          { label: t('common.import_type.shipping'), value: 'shipping' },
          { label: t('common.import_type.product_sku'), value: 'product_sku' },
          { label: t('common.import_type.inventory_monitor'), value: 'inventory_monitor' },
          { label: t('common.import_type.shelf'), value: 'shelf' },
          { label: t('common.import_type.contact_address'), value: 'contact_address' },
          { label: t('common.import_type.sales_channel_relate_product'), value: 'sales_channel_relate_product' },
          { label: t('common.import_type.logistic_data'), value: 'logistic_data' },
          { label: t('common.import_type.stock_adjustment'), value: 'stock_adjustment' },
          { label: t('common.import_type.inventory'), value: 'inventory' },
        ],
      },
      {
        label: t(`shared.imports.list.filters.status`),
        component: MultipleValue,
        inputName: 'imports.status',
        defaultPinned: true,
        createable: false,
        defaultOptions: [
          { label: t('common.import_status.pending'), value: 'pending' },
          { label: t('common.import_status.parsing'), value: 'parsing' },
          { label: t('common.import_status.importing'), value: 'importing' },
          { label: t('common.import_status.partially_completed'), value: 'partially_completed' },
          { label: t('common.import_status.completed'), value: 'completed' },
          { label: t('common.import_status.failed'), value: 'failed' },
        ],
      }
    ];

    // Add merchant filter for operator role (single-merchant 模式隱藏)
    if (role === 'operator' && !hideMerchantUI) {
      rules.push({
        label: t(`shared.imports.list.filters.merchant`),
        component: MultipleValue,
        inputName: 'imports.merchant_id',
        createable: false,
        defaultOptions: merchants.map((merchant) => ({
          label: merchant.name,
          value: merchant.id,
        })),
      });
    }

    rules.push(
      {
        label: t(`common.created_at`),
        component: FullDayRangePicker,

        defaultPinned: true,
        sortable: false,
        inputName: 'imports.created_at',
      },
      {
        label: t(`common.updated_at`),
        component: FullDayRangePicker,

        sortable: false,
        inputName: 'imports.updated_at',
      }
    );

    return rules;
  }, [role, t, merchants, hideMerchantUI])

  return (
    <TableIndex
      columns={columns}
      searchable={true}
      columnsEditable={true}
      tableId={`${role === 'operator' ? 'Operator' : 'Admin'}ImportsTable`}
      defaultQuery={{
        sorting_field: 'imports.created_at',
        sorting_direction: 'desc',
      }}
      supportRules={supportRules}
      lastModified={lastModified}
      setLastModified={setLastModified}
      service={service}
      filterHelpExample={t('shared.imports.list.filter_help_example') as string}
      filterDocsUrl={`/docs/${role}/imports/list#features`}
    />
  )
}

export default ImportsList
